import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CrmNotesCard, { deriveNoteTitle } from '../../shared/components/CrmNotesCard';
import NoteModal from './NoteModal';

const OrderNotesCard = ({ order, csrfToken, notesUrl }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [editingNote, setEditingNote] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    axios.get(`${notesUrl}.json`, { params: { notable_type: 'Order', notable_id: order.id } })
      .then(({ data }) => { if (active) setNotes(data || []); })
      .catch(() => { if (active) setError('Failed to load notes'); })
      .then(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [notesUrl, order.id]);

  const createNote = async (noteBody) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    const { data } = await axios.post(`${notesUrl}.json`, {
      note: {
        title: deriveNoteTitle(noteBody),
        body: noteBody,
        notable_type: 'Order',
        notable_id: order.id,
      },
    });
    setNotes((prev) => [data, ...prev]);
    setError('');
  };

  const openEditModal = (note) => {
    setEditingNote(note);
    setTitle(note.title || '');
    setBody(note.body || '');
    setModalError('');
  };

  const closeModal = () => setEditingNote(null);

  const saveNote = (e) => {
    e.preventDefault();
    if (!body.trim() || !editingNote) return;
    setSubmitting(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.patch(`${notesUrl}/${editingNote.id}.json`, { note: { title, body } })
      .then(({ data }) => {
        setNotes((prev) => prev.map((n) => (n.id === data.id ? data : n)));
        setEditingNote(null);
      })
      .catch(() => setModalError('Failed to update note'))
      .then(() => setSubmitting(false));
  };

  const deleteNote = (note) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.delete(`${notesUrl}/${note.id}.json`)
      .then(() => setNotes((prev) => prev.filter((n) => n.id !== note.id)))
      .catch(() => {
        // eslint-disable-next-line no-alert
        alert('Failed to delete note');
      });
  };

  return (
    <div className="op-card">
      <CrmNotesCard
        notes={notes}
        loading={loading}
        error={error}
        onCreateNote={createNote}
        onEditNote={openEditModal}
        onDeleteNote={deleteNote}
        canManageNotes
      />
      <NoteModal
        open={Boolean(editingNote)}
        onClose={closeModal}
        onSubmit={saveNote}
        title={title}
        setTitle={setTitle}
        body={body}
        setBody={setBody}
        submitting={submitting}
        error={modalError}
      />
    </div>
  );
};

export default OrderNotesCard;
